import { useEffect, useMemo, useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { useLocation, useParams } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  FileText,
  Lock,
  ShieldCheck,
  X,
  Pencil,
  CreditCard,
  AlertTriangle,
} from "lucide-react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SunLogo } from "@/components/SunLogo";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, API_BASE } from "@/lib/queryClient";
import { formatDate, formatMoney } from "@/lib/format";
// pdfjs-dist browser ESM build + worker
// @ts-ignore - no types for the .mjs entry
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs";
// Import the worker as a URL — Vite will bundle and serve it
// @ts-ignore
import pdfWorkerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";

// Configure worker once
(pdfjsLib as any).GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

interface AnchorCoords {
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  pageWidth: number;
  pageHeight: number;
  placement?: "inline-right" | "below-section" | "signature-line";
  // Stamp offsets + dimensions from the AnchorRule snapshot embedded at
  // contract-create time. When present, these come from the server-side
  // anchor rules (Settings UI) and are what the server will use when it
  // stamps the signed PDF. The Sign page renders its overlay boxes using
  // these exact values so the click target matches the eventual stamp
  // position pixel-for-pixel. Optional for back-compat with older contracts
  // whose anchor_map predates the snapshot column — the overlay falls back
  // to hardcoded constants (8pt / 12pt right-shift, fixed box sizes) in
  // that case.
  stampXOffset?: number;
  stampYOffset?: number;
  stampWidth?: number;
  stampHeight?: number;
}

interface InitialField {
  key: string;
  label: string;
  description: string;
  coords: AnchorCoords | null;
}

interface SignerView {
  id: string;
  contractTitle: string;
  signerName: string;
  signerEmail: string;
  eventDate: string | null;
  totalValueCents: number;
  depositAmountCents: number;
  status: string;
  signedAt: string | null;
  paidAt?: string | null;
  porCntr: string | null;
  templateName?: string;
  initialFields: InitialField[];
  signatureAnchor: AnchorCoords | null;
  // Revision auto-charge: when this signing represents an updated contract
  // for a customer who already paid an initial deposit, the worker surfaces
  // the delta amount + the saved card brand/last4 so we can disclose to
  // the customer that the saved card will be charged at signature time.
  revisionPaymentDueCents?: number | null;
  depositTopUp?: { dueCents: number; requireFullPayment: boolean } | null;
  savedCard?: { brand: string; last4: string } | null;
  // "Charge now" path: when the operator already charged the saved card
  // at resend time, the Sign page replaces the disclosure with a confirmation
  // and the sign endpoint skips its post-signature auto-charge.
  revisionAlreadyCharged?: boolean;
  revisionAlreadyChargedAmount?: number;
  // Account-customer sign-only flow. When false, the customer signs under
  // Net 30 terms and no payment is collected through this flow — the submit
  // button reads "Sign document" instead of "Sign and proceed to payment".
  paymentRequired?: boolean;
}

// revisionCharge field returned from /sign endpoint when the worker has
// auto-charged the saved card after capturing the signature.
type RevisionChargeResult =
  | { status: "succeeded"; amount?: number; paymentIntentId?: string }
  | {
      status: "requires_action";
      amount?: number;
      clientSecret: string;
      paymentIntentId?: string;
    }
  | { status: "failed"; amount?: number; message?: string; code?: string }
  | { status: "skipped" };

// Stripe.js loader — mirror SignComplete.tsx so we share a singleton
// promise. Only used on the requires_action 3DS branch.
const PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe() {
  if (!stripePromise) {
    stripePromise = PUBLISHABLE_KEY ? loadStripe(PUBLISHABLE_KEY) : Promise.resolve(null);
  }
  return stripePromise;
}

type FontStyleKey = "great-vibes" | "dancing-script" | "caveat";

interface SignatureMark {
  mode: "type" | "draw";
  fullName: string;
  initials: string;
  fontStyle: FontStyleKey;
  signatureDataUrl?: string;
  initialsDataUrl?: string;
}

const FONT_STYLES: { key: FontStyleKey; label: string; cssVar: string; sample: string }[] = [
  { key: "great-vibes", label: "Formal", cssVar: "'Great Vibes', cursive", sample: "Aa" },
  { key: "dancing-script", label: "Standard", cssVar: "'Dancing Script', cursive", sample: "Aa" },
  { key: "caveat", label: "Casual", cssVar: "'Caveat', cursive", sample: "Aa" },
];

// Sizes for overlay boxes (CSS pixels at scale=1.5; scale-aware below)
// Larger, more visible click targets so signers can't miss them.
const INITIAL_W_BASE = 96;
const INITIAL_H_BASE = 34;
const SIGNATURE_W_BASE = 260;
const SIGNATURE_H_BASE = 44;

interface PageSize {
  cssWidth: number;
  cssHeight: number;
  pdfWidth: number;
  pdfHeight: number;
  scale: number;
}

function deriveInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .slice(0, 3)
    .join("");
}

function fontCssVar(key: FontStyleKey): string {
  return FONT_STYLES.find((f) => f.key === key)?.cssVar ?? "'Dancing Script', cursive";
}

/**
 * Compute overlay CSS coords from PDF user-space coords.
 *
 * The overlay must land in the SAME spot the server will stamp the
 * signature/initial when the customer signs. The server uses (in worker/pdf.ts):
 *
 *   x = hit.x + hit.width + rule.xOffset
 *   y = hit.y + rule.yOffset                  // PDF user-space, y up
 *   stampW = rule.width
 *   stampH = rule.height
 *
 * When `coords.stampXOffset` etc. are present (modern API response that
 * includes the AnchorRule snapshot), we mirror that math exactly. When
 * absent (legacy contracts whose anchor_map predates the snapshot column),
 * we fall back to the original hardcoded constants for back-compat.
 */
function anchorToOverlay(
  coords: AnchorCoords,
  pageHeightPts: number,
  scale: number,
  isSignature: boolean,
): { left: number; top: number; width: number; height: number } {
  const hasRuleOffsets =
    typeof coords.stampXOffset === "number" &&
    typeof coords.stampYOffset === "number" &&
    typeof coords.stampWidth === "number" &&
    typeof coords.stampHeight === "number";

  if (hasRuleOffsets) {
    // ---- Settings-driven path ------------------------------------------------
    // Position and size the overlay to match the server's stamp 1:1, using
    // the SAME formula as /api/anchor-overrides/preview (worker/routes/
    // anchor-overrides.ts) and the live stamper (worker/pdf.ts):
    //
    //   bx     = hit.x + hit.width + rule.xOffset
    //   by     = hit.y + rule.yOffset           // PDF y-up, scanner-normalized
    //   width  = rule.width
    //   height = rule.height
    //
    // The PDF scanner already normalizes y into standard PDF user-space
    // (bottom-up, positive) by adding pageHeight when the source has a
    // flipped CTM, so we always use `pageHeight - y` to convert to CSS top.
    //
    // No min-size clamp: the box must be EXACTLY the rule dimensions so the
    // visible overlay matches the post-sign stamp pixel-for-pixel.
    const xOffsetPts = coords.stampXOffset as number;
    const yOffsetPts = coords.stampYOffset as number;
    const stampWPts = coords.stampWidth as number;
    const stampHPts = coords.stampHeight as number;

    const stampLeftPts = coords.x + coords.width + xOffsetPts;
    const stampBottomPts = coords.y + yOffsetPts;
    // CSS top of the stamp's TOP edge = pageHeight - (bottom + height)
    const cssTop = (pageHeightPts - stampBottomPts - stampHPts) * scale;
    const cssLeft = stampLeftPts * scale;
    const cssW = stampWPts * scale;
    const cssH = stampHPts * scale;

    return {
      left: cssLeft,
      top: cssTop,
      width: cssW,
      height: cssH,
    };
  }

  // ---- Legacy / fallback path ---------------------------------------------
  // Used only for contracts whose anchor_map snapshot predates the offsets.
  // Original behaviour: hardcoded 8pt / 12pt right-shift + fixed base sizes,
  // box bottom anchored 2pt above the matched-text baseline.
  const sizeScale = scale / 1.5;
  const wRaw = (isSignature ? SIGNATURE_W_BASE : INITIAL_W_BASE) * sizeScale;
  const hRaw = (isSignature ? SIGNATURE_H_BASE : INITIAL_H_BASE) * sizeScale;
  const w = Math.max(wRaw, isSignature ? 120 : 56);
  const h = Math.max(hRaw, 22);
  const dxPts = isSignature ? 12 : 8;
  const leftPts = coords.x + coords.width + dxPts;
  const distFromTopPts = coords.y <= 0 ? -coords.y : pageHeightPts - coords.y;
  const baselineCssY = distFromTopPts * scale;
  const baselineGap = 2 * scale;
  return {
    left: leftPts * scale,
    top: baselineCssY - h + baselineGap,
    width: w,
    height: h,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────────

export default function Sign() {
  const { token } = useParams<{ token: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  // Adopted reusable mark + per-anchor application state
  const [mark, setMark] = useState<SignatureMark | null>(null);
  const [filled, setFilled] = useState<Record<string, boolean>>({});
  const [modalOpen, setModalOpen] = useState(false);
  const pendingApplyRef = useRef<string | null>(null); // anchor key that triggered the modal
  const [eConsent, setEConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);

  const { data, isLoading, isError } = useQuery<SignerView>({
    queryKey: [`/api/contracts/by-token/${token}`],
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Log "viewed" once
  useEffect(() => {
    if (!data) return;
    apiRequest("POST", `/api/contracts/by-token/${token}/view`).catch(() => {});
  }, [data, token]);

  // Option A: if the customer reopens the link after signing, route them to
  // the completion page (which handles BOTH unpaid → <PaymentStep> and paid
  // → <PaidSuccess> with a full receipt). The bare "already signed" screen
  // below at status==="signed" left no payment receipt visible, which made
  // customers think their payment failed when they came back (see
  // contract 212271 / Bailey Exler, 2026-05-22). The signed PDF is preserved.
  useEffect(() => {
    if (!data) return;
    if (data.status === "signed") {
      navigate(`/sign/${token}/complete`);
    }
  }, [data, token, navigate]);

  const allInitialsFilled = useMemo(() => {
    if (!data?.initialFields?.length) return true;
    return data.initialFields.every((f) => !!filled[f.key]);
  }, [data?.initialFields, filled]);

  const signatureFilled = useMemo(() => {
    if (!data?.signatureAnchor) return true; // no signature anchor → trivially complete
    return !!filled["signature"];
  }, [data, filled]);

  const filledCount = useMemo(() => {
    let n = 0;
    if (!data) return 0;
    for (const f of data.initialFields) if (filled[f.key]) n++;
    if (data.signatureAnchor && filled["signature"]) n++;
    return n;
  }, [data, filled]);

  const totalCount = useMemo(() => {
    if (!data) return 0;
    return data.initialFields.length + (data.signatureAnchor ? 1 : 0);
  }, [data]);

  const canSubmit =
    !!mark && allInitialsFilled && signatureFilled && eConsent && termsConsent;

  const onClickOverlay = useCallback(
    (key: string, currentlyFilled: boolean) => {
      if (currentlyFilled) {
        // toggle off (clear individual mark)
        setFilled((prev) => {
          const { [key]: _, ...rest } = prev;
          return rest;
        });
        return;
      }
      if (!mark) {
        pendingApplyRef.current = key;
        setModalOpen(true);
        return;
      }
      // already adopted → just stamp it in
      setFilled((prev) => ({ ...prev, [key]: true }));
    },
    [mark],
  );

  const onAdopt = useCallback((m: SignatureMark) => {
    setMark(m);
    setModalOpen(false);
    const k = pendingApplyRef.current;
    pendingApplyRef.current = null;
    if (k) {
      setFilled((prev) => ({ ...prev, [k]: true }));
    }
  }, []);

  // Inline 3DS state — surfaces a brief overlay while we authenticate the
  // saved card via Stripe.js confirmCardPayment. Cleared on success/failure.
  const [threeDSecureState, setThreeDSecureState] = useState<
    null | { status: "running" } | { status: "failed"; message: string }
  >(null);

  // Hard-decline state when the saved card fails non-3DS (insufficient
  // funds, card declined, etc.). We surface a recovery CTA that routes the
  // customer to the existing /sign/:token/repay flow where they can enter
  // a brand-new card.
  const [chargeFailure, setChargeFailure] = useState<
    null | { message: string; code?: string }
  >(null);

  const signMut = useMutation({
    mutationFn: async () => {
      if (!mark) throw new Error("Adopt a signature first");
      const initialsByAnchor: Record<string, true> = {};
      for (const f of data?.initialFields ?? []) {
        if (filled[f.key]) initialsByAnchor[f.key] = true;
      }
      const eConsentText =
        "I consent to use electronic records and electronic signatures for this transaction under the U.S. ESIGN Act and the Ohio Uniform Electronic Transactions Act, and I confirm I am able to access and retain a copy of the signed contract in PDF form.";
      const termsConsentText =
        "I have read the rental contract above and agree to be legally bound by its terms. I authorize Sun Rental Center to apply my electronic signature to this contract.";
      const clientMeta = {
        screenWidth: typeof screen !== "undefined" ? screen.width : null,
        screenHeight: typeof screen !== "undefined" ? screen.height : null,
        viewportWidth: typeof window !== "undefined" ? window.innerWidth : null,
        viewportHeight: typeof window !== "undefined" ? window.innerHeight : null,
        devicePixelRatio: typeof window !== "undefined" ? window.devicePixelRatio : null,
        language:
          typeof navigator !== "undefined"
            ? navigator.language ?? null
            : null,
        languages:
          typeof navigator !== "undefined" && Array.isArray((navigator as any).languages)
            ? ((navigator as any).languages as string[]).slice(0, 5)
            : null,
        timezone:
          typeof Intl !== "undefined"
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : null,
        timezoneOffsetMinutes: new Date().getTimezoneOffset(),
        platform:
          typeof navigator !== "undefined"
            ? (navigator as any).platform ?? null
            : null,
      };
      const payload = {
        fullName: mark.fullName,
        initials: mark.initials,
        mode: mark.mode,
        fontStyle: mark.fontStyle,
        signatureDataUrl: mark.signatureDataUrl,
        initialsDataUrl: mark.initialsDataUrl,
        consent: {
          agreed: true,
          agreedAt: new Date().toISOString(),
          eConsent: true,
          eConsentText,
          termsConsent: true,
          termsConsentText,
        },
        clientMeta,
        initialsByAnchor,
        signatureFilled: !!filled["signature"],
      };
      const res = await apiRequest("POST", `/api/contracts/by-token/${token}/sign`, payload);
      const json = await res.json();
      return json as { ok: boolean; revisionCharge?: RevisionChargeResult };
    },
    onSuccess: async (resp) => {
      // Reset prior failure UI — if the customer recovered after a hard
      // decline by signing again, we don't want stale red banners.
      setChargeFailure(null);

      const charge = resp.revisionCharge;

      // No revision auto-charge needed (no saved card, no delta, or just a
      // first-time signature) — fall through to the standard payment page.
      if (!charge || charge.status === "succeeded" || charge.status === "skipped") {
        navigate(`/sign/${token}/complete`);
        return;
      }

      // 3DS challenge — launch Stripe.js to handle the authentication, then
      // hit /payment-confirm so the worker can mark the contract paid.
      if (charge.status === "requires_action") {
        setThreeDSecureState({ status: "running" });
        const stripe = await getStripe();
        if (!stripe) {
          // Stripe.js failed to load — punt to the standard re-pay flow so
          // the customer can enter a card without 3DS interception.
          setThreeDSecureState({
            status: "failed",
            message:
              "Saved card requires verification but Stripe failed to load. Please pay with a different card.",
          });
          navigate(`/sign/${token}/repay`);
          return;
        }
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          charge.clientSecret,
        );
        if (error || !paymentIntent || paymentIntent.status !== "succeeded") {
          const message =
            error?.message ||
            `Card verification ${paymentIntent?.status || "did not complete"}. Please pay with a different card.`;
          setThreeDSecureState({ status: "failed", message });
          setChargeFailure({ message, code: "3ds_failed" });
          return;
        }
        try {
          await apiRequest(
            "POST",
            `/api/contracts/by-token/${token}/payment-confirm`,
          );
        } catch (err: any) {
          // Payment intent succeeded; reconciliation will catch up via
          // webhook. Continue to complete page either way.
        }
        setThreeDSecureState(null);
        navigate(`/sign/${token}/complete`);
        return;
      }

      // Hard decline (no 3DS) — the contract IS signed (saved on the worker)
      // but the saved card was rejected. Surface a recovery CTA that takes
      // the customer to /repay where they can enter a fresh card.
      if (charge.status === "failed") {
        setChargeFailure({
          message:
            charge.message ||
            "Your saved card was declined. Please pay with a different card.",
          code: charge.code,
        });
        return;
      }
    },
    onError: (err: any) => {
      toast({
        title: "Could not sign",
        description: err?.message ?? "Please try again.",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <SignShell>
        <div className="text-center text-sm text-muted-foreground py-20">Loading contract…</div>
      </SignShell>
    );
  }
  if (isError || !data) {
    return (
      <SignShell>
        <div className="text-center py-20">
          <h1 className="text-xl font-semibold">Contract not found</h1>
          <p className="text-sm text-muted-foreground mt-2">
            This signing link may have been voided or is no longer valid.
          </p>
        </div>
      </SignShell>
    );
  }

  if (data.status === "signed") {
    return (
      <SignShell>
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8">
          <div className="text-center max-w-md mx-auto">
            <div className="h-14 w-14 rounded-full bg-[hsl(var(--status-signed))]/10 text-[hsl(var(--status-signed))] mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h1 className="text-xl font-semibold">This contract has been signed</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Thank you, {data.signerName}. A copy is on file with Sun Rental.
            </p>
          </div>
          <div className="mt-8 rounded-xl border border-card-border bg-card overflow-hidden">
            <div className="px-4 py-2.5 border-b border-card-border flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <FileText className="h-3.5 w-3.5" />
                Signed contract
              </div>
              <a
                href={`${API_BASE}/api/contracts/by-token/${token}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Open in new tab ↗
              </a>
            </div>
            <PdfStack pdfUrl={`${API_BASE}/api/contracts/by-token/${token}/pdf`} />
          </div>
        </div>
      </SignShell>
    );
  }

  if (data.status === "voided") {
    return (
      <SignShell>
        <div className="text-center py-16 max-w-md mx-auto">
          <h1 className="text-xl font-semibold">This contract has been voided</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Please contact Sun Rental at 440-942-6106 if you have questions.
          </p>
        </div>
      </SignShell>
    );
  }

  // Per-signing void or re-send supersession — the customer is on an old
  // link. A newer revision likely exists; we tell them to check email/text
  // for the latest link rather than implying the contract itself is dead.
  if (data.status === "superseded") {
    return (
      <SignShell>
        <div className="text-center py-16 max-w-md mx-auto">
          <h1 className="text-xl font-semibold">This signing link is no longer active</h1>
          <p className="text-sm text-muted-foreground mt-2">
            A newer version of this contract may have been sent. Please check your
            email or text messages for the latest link, or contact Sun Rental at
            440-942-6106.
          </p>
        </div>
      </SignShell>
    );
  }

  if ((data as any).isExpired) {
    const exp = (data as any).expiresAt as string | undefined;
    return (
      <SignShell>
        <div className="text-center py-16 max-w-md mx-auto">
          <h1 className="text-xl font-semibold">This signing link has expired</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {exp
              ? `It expired on ${new Date(exp).toLocaleString(undefined, {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}.`
              : null}{" "}
            Please contact Sun Rental at 440-942-6106 to request a new link.
          </p>
        </div>
      </SignShell>
    );
  }

  // ────────── Active signing UI ──────────

  const todayStr = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Greeting row — centered band beneath the header
  const headerContext = (
    <div className="py-3 relative">
      <p className="text-sm sm:text-[15px] leading-snug text-foreground text-center px-4 sm:px-12">
        Hi <span className="font-medium">{data.signerName.split(" ")[0]}</span>, carefully review the agreement below and tap each <span className="font-medium">Initial</span> or <span className="font-medium">Sign</span> box on the contract below to add your mark.
      </p>
      {mark && (
        <button
          type="button"
          onClick={() => {
            pendingApplyRef.current = null;
            setModalOpen(true);
          }}
          className="hidden sm:inline-flex absolute right-4 top-1/2 -translate-y-1/2 items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-md px-2.5 py-1.5"
          data-testid="button-edit-signature"
        >
          <Pencil className="h-3.5 w-3.5" /> Edit signature
        </button>
      )}
    </div>
  );

  // Contract title slot — centered on desktop, right-aligned & wrapping on mobile
  const titleSlot = (
    <div className="flex items-center justify-end md:justify-center gap-3 min-w-0">
      <div className="text-sm md:text-base font-semibold tracking-tight leading-tight break-words md:truncate">
        {data.contractTitle}
        {data.porCntr && (
          <span
            className="text-muted-foreground font-mono font-normal ml-2 whitespace-nowrap"
            data-testid="text-por-cntr"
          >
            #{data.porCntr}
          </span>
        )}
      </div>
      <div className="hidden md:flex items-center gap-1.5 text-[11px] text-muted-foreground shrink-0 border border-border rounded-full px-2 py-0.5">
        <ShieldCheck className="h-3 w-3" /> Secure
      </div>
    </div>
  );

  // Progress slot — right side of the header bar on desktop, edge-to-edge on mobile.
  // Labels stay inset; only the bar itself stretches to the screen edges.
  const progressSlot = (
    <div className="w-full md:max-w-[260px]">
      <div className="flex items-center justify-between text-[11px] mb-1 px-4 sm:px-6 md:px-0">
        <span className="text-muted-foreground" data-testid="text-progress">
          <span className="font-medium text-foreground tabular-nums">{filledCount}</span> of {totalCount} fields complete
        </span>
        <span className="text-muted-foreground tabular-nums">
          {totalCount ? Math.round((filledCount / totalCount) * 100) : 0}%
        </span>
      </div>
      <div className="h-1.5 w-full bg-muted overflow-hidden md:rounded-full">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: totalCount ? `${(filledCount / totalCount) * 100}%` : "0%" }}
        />
      </div>
    </div>
  );

  return (
    <SignShell headerExtras={headerContext} titleSlot={titleSlot} rightSlot={progressSlot}>
      <div className="max-w-[1100px] mx-auto px-0 sm:px-6 py-6 md:py-8">
        {data.depositTopUp && (
          <div className="mx-4 sm:mx-0 mb-5 rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm" data-testid="sign-top-up-disclosure">
            <div className="font-medium">
              {data.depositTopUp.requireFullPayment ? "Full remaining balance" : "50% deposit top-up"}: {formatMoney(data.depositTopUp.dueCents)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Sign this updated contract first, then complete payment.
              Nothing is charged before your signature.
              {data.depositTopUp.requireFullPayment && " Full payment is required within 14 days."}
            </p>
          </div>
        )}
        {/* PDF + overlays — edge-to-edge on mobile, padded on tablet+ */}
        <PdfWithOverlays
          pdfUrl={`${API_BASE}/api/contracts/by-token/${token}/pdf`}
          initialFields={data.initialFields}
          signatureAnchor={data.signatureAnchor}
          mark={mark}
          filled={filled}
          onClickOverlay={onClickOverlay}
        />

        {/* Official signature page */}
        <OfficialSignaturePage
          contractTitle={data.contractTitle}
          porCntr={data.porCntr}
          signerName={mark?.fullName || data.signerName}
          signerEmail={data.signerEmail}
          mark={mark}
          today={todayStr}
          eConsent={eConsent}
          setEConsent={setEConsent}
          termsConsent={termsConsent}
          setTermsConsent={setTermsConsent}
          canSubmit={canSubmit}
          isSubmitting={signMut.isPending || threeDSecureState?.status === "running"}
          onSubmit={() => signMut.mutate()}
          allInitialsFilled={allInitialsFilled}
          signatureFilled={signatureFilled}
          revisionPaymentDueCents={data.revisionPaymentDueCents ?? 0}
          savedCard={data.savedCard ?? null}
          revisionAlreadyCharged={!!data.revisionAlreadyCharged}
          revisionAlreadyChargedAmount={data.revisionAlreadyChargedAmount ?? 0}
          paymentRequired={data.paymentRequired !== false}
          chargeFailure={chargeFailure}
          threeDSecure={threeDSecureState}
          onRetryWithNewCard={() => navigate(`/sign/${token}/repay`)}
        />
      </div>

      <AdoptSignatureDialog
        open={modalOpen}
        onOpenChange={(o) => {
          setModalOpen(o);
          if (!o) pendingApplyRef.current = null;
        }}
        defaultName={mark?.fullName || data.signerName}
        defaultInitials={mark?.initials || deriveInitials(data.signerName)}
        defaultFont={mark?.fontStyle || "dancing-script"}
        onAdopt={onAdopt}
      />
    </SignShell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// PDF stack (read-only, used for the "already signed" branch)
// ────────────────────────────────────────────────────────────────────────────

function PdfStack({ pdfUrl }: { pdfUrl: string }) {
  return (
    <PdfWithOverlays
      pdfUrl={pdfUrl}
      initialFields={[]}
      signatureAnchor={null}
      mark={null}
      filled={{}}
      onClickOverlay={() => {}}
      readOnly
    />
  );
}

// ────────────────────────────────────────────────────────────────────────────
// PDF + overlays (no inner scroll — flows in the page)
// ────────────────────────────────────────────────────────────────────────────

interface PdfWithOverlaysProps {
  pdfUrl: string;
  initialFields: InitialField[];
  signatureAnchor: AnchorCoords | null;
  mark: SignatureMark | null;
  filled: Record<string, boolean>;
  onClickOverlay: (key: string, currentlyFilled: boolean) => void;
  readOnly?: boolean;
}

function PdfWithOverlays({
  pdfUrl,
  initialFields,
  signatureAnchor,
  mark,
  filled,
  onClickOverlay,
  readOnly = false,
}: PdfWithOverlaysProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [pageSizes, setPageSizes] = useState<PageSize[]>([]);
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const pdfRef = useRef<any>(null);

  // Observe the column width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = e.contentRect.width;
        if (w > 0) setContainerWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Load doc
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(pdfUrl);
        if (!res.ok) throw new Error(`PDF ${res.status}`);
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        const loadingTask = (pdfjsLib as any).getDocument({
          data: buf.slice(0),
          disableFontFace: false,
          isEvalSupported: false,
        });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        setNumPages(pdf.numPages);
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message || "Could not load PDF");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  // Render pages — fill the column, target ~1.6x scale on US Letter
  useEffect(() => {
    let cancelled = false;
    if (containerWidth === 0 || !pdfRef.current || numPages === 0) return;
    (async () => {
      const pdf = pdfRef.current;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const sizes: PageSize[] = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const baseVp = page.getViewport({ scale: 1 });
        // Clamp to container width; on big columns hit ~1.7x for readability
        const targetCssWidth = Math.min(containerWidth, baseVp.width * 1.75);
        const scale = targetCssWidth / baseVp.width;
        const vp = page.getViewport({ scale });
        const canvas = canvasRefs.current[i - 1];
        if (!canvas) continue;
        canvas.width = Math.floor(vp.width * dpr);
        canvas.height = Math.floor(vp.height * dpr);
        canvas.style.width = `${vp.width}px`;
        canvas.style.height = `${vp.height}px`;
        const ctx = canvas.getContext("2d");
        if (!ctx) continue;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        await page.render({ canvasContext: ctx, viewport: vp, canvas }).promise;
        sizes[i - 1] = {
          cssWidth: vp.width,
          cssHeight: vp.height,
          pdfWidth: baseVp.width,
          pdfHeight: baseVp.height,
          scale,
        };
        if (cancelled) return;
      }
      if (!cancelled) setPageSizes(sizes);
    })();
    return () => {
      cancelled = true;
    };
  }, [numPages, containerWidth]);

  const overlaysByPage = useMemo(() => {
    const map: Record<number, { key: string; coords: AnchorCoords; isSig: boolean }[]> = {};
    if (readOnly) return map;
    for (const f of initialFields) {
      if (!f.coords) continue;
      const arr = (map[f.coords.page] ||= []);
      arr.push({ key: f.key, coords: f.coords, isSig: false });
    }
    if (signatureAnchor) {
      const arr = (map[signatureAnchor.page] ||= []);
      arr.push({ key: "signature", coords: signatureAnchor, isSig: true });
    }
    return map;
  }, [initialFields, signatureAnchor, readOnly]);

  const pageCount = numPages || 1;

  return (
    <div
      ref={containerRef}
      className="relative w-full bg-muted/30 border border-t-0 border-card-border rounded-b-xl"
      data-testid="pdf-viewer"
    >
      {loadError && (
        <div className="p-6 text-sm text-destructive">Could not load PDF: {loadError}</div>
      )}
      <div className="flex flex-col items-center gap-6 py-5 px-2 sm:px-3">
        {Array.from({ length: pageCount }).map((_, idx) => {
          const size = pageSizes[idx];
          const overlays = overlaysByPage[idx] ?? [];
          return (
            <div key={idx} className="relative" style={{ width: size?.cssWidth }}>
              <canvas
                ref={(el) => {
                  canvasRefs.current[idx] = el;
                }}
                className="block shadow-md bg-white rounded-sm"
                data-testid={`pdf-canvas-${idx}`}
              />
              {/* Overlay layer sized to the canvas.
                  Note: the red gutter triangles that previously pointed at
                  each empty signature/initial row were removed at the user's
                  request — the amber "Sign Here" / "Initial" pills already
                  signal where to click. Do not re-add gutter arrows. */}
              {size && overlays.length > 0 && (() => {
                const positioned = overlays.map((ov) => ({
                  ov,
                  pos: anchorToOverlay(ov.coords, size.pdfHeight, size.scale, ov.isSig),
                  isFilled: !!filled[ov.key],
                }));
                return (
                  <div
                    className="absolute left-0 top-0 pointer-events-none"
                    style={{ width: size.cssWidth, height: size.cssHeight }}
                  >
                    {positioned.map(({ ov, pos, isFilled }) => (
                      <AnchorPill
                        key={ov.key}
                        ov={ov}
                        pos={pos}
                        filled={isFilled}
                        mark={mark}
                        onClick={() => onClickOverlay(ov.key, isFilled)}
                      />
                    ))}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// One overlay box (initial or signature)
// ────────────────────────────────────────────────────────────────────────────

function AnchorPill({
  ov,
  pos,
  filled,
  mark,
  onClick,
}: {
  ov: { key: string; coords: AnchorCoords; isSig: boolean };
  pos: { left: number; top: number; width: number; height: number };
  filled: boolean;
  mark: SignatureMark | null;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);
  const empty = !filled;

  // The stamp footprint (pos) is intentionally exact so the rendered
  // signature/initial lands EXACTLY where the server stamps it. But for
  // small initial stamps (e.g. 26×16 pts ≈ 39×24 css px) that's a tiny
  // click target with no room for a label. So the visual indicator
  // (halo + label tag) is decoupled from the stamp footprint:
  //
  //   - The clickable amber box stays exactly the stamp size (so the
  //     rendered mark fills the actual stamp area 1:1 when filled).
  //   - When EMPTY, we render a larger halo CENTERED on the stamp box
  //     and a label tag attached to the halo. Both are decorative and
  //     extend beyond pos; they don't change the stamp position.
  //   - Min halo size guarantees a tappable target on mobile.
  const haloMinW = ov.isSig ? 160 : 80;
  const haloMinH = ov.isSig ? 40 : 32;
  // Pad the halo around the stamp; clamp to minimums.
  const haloPadX = 10;
  const haloPadY = 6;
  const haloW = Math.max(pos.width + haloPadX * 2, haloMinW);
  const haloH = Math.max(pos.height + haloPadY * 2, haloMinH);
  // Halo offset (relative to the stamp box) so it stays centered on the
  // stamp box even when min-clamped to a larger size.
  const haloLeft = (pos.width - haloW) / 2;
  const haloTop = (pos.height - haloH) / 2;

  return (
    <div
      className="absolute pointer-events-auto"
      style={{ left: pos.left, top: pos.top, width: pos.width, height: pos.height }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Decorative halo (empty state only) — sits BEHIND the stamp box,
          centered on it, larger than the stamp. Captures clicks too so the
          whole highlighted area is tappable. Thin red outline + amber fill
          (no label inside or above — the halo alone signals the target). */}
      {empty && (
        <button
          type="button"
          onClick={onClick}
          aria-hidden="true"
          tabIndex={-1}
          className="absolute rounded-lg border border-[hsl(358_78%_48%)] bg-amber-300/60 dark:bg-amber-400/60 hover:bg-amber-300/80 dark:hover:bg-amber-400/80 cursor-pointer animate-anchor-attention shadow-md transition"
          style={{
            left: haloLeft,
            top: haloTop,
            width: haloW,
            height: haloH,
            zIndex: 0,
          }}
          data-testid={`overlay-halo-${ov.key}`}
        />
      )}

      {/* The actual stamp footprint — EXACT size so the rendered mark lands
          1:1 where the server stamps. When empty, this is a transparent
          inner click target sitting on top of the halo so clicks land on
          the same handler. When filled, this is where the mark renders. */}
      <button
        type="button"
        onClick={onClick}
        className={
          "group relative w-full h-full flex items-center justify-center select-none rounded-sm transition " +
          (empty
            ? "bg-transparent cursor-pointer"
            : "bg-transparent cursor-pointer hover:bg-primary/5")
        }
        style={{ zIndex: 1 }}
        data-testid={`overlay-${ov.key}`}
        data-filled={filled ? "true" : "false"}
        title={
          empty
            ? `Click to add ${ov.isSig ? "signature" : "initial"}${mark ? "" : " (you'll create yours first)"}`
            : `Click to clear`
        }
      >
        {!empty && (
          <RenderedMark mark={mark} isSig={ov.isSig} boxWidth={pos.width} boxHeight={pos.height} />
        )}
      </button>
      {filled && hover && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-foreground text-background flex items-center justify-center shadow"
          style={{ zIndex: 3 }}
          aria-label="Clear"
          data-testid={`clear-${ov.key}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function RenderedMark({
  mark,
  isSig,
  boxWidth,
  boxHeight,
}: {
  mark: SignatureMark | null;
  isSig: boolean;
  boxWidth: number;
  boxHeight: number;
}) {
  if (!mark) return null;
  if (mark.mode === "draw") {
    const url = isSig ? mark.signatureDataUrl : mark.initialsDataUrl;
    if (!url) return null;
    return (
      <img
        src={url}
        alt={isSig ? "Signature" : "Initials"}
        className="max-h-full max-w-full object-contain"
        style={{ height: boxHeight - 2, width: "auto" }}
      />
    );
  }
  // typed
  if (isSig) {
    return (
      <span
        className="leading-none whitespace-nowrap text-[hsl(220,55%,25%)] dark:text-[hsl(35,90%,80%)]"
        style={{
          fontFamily: fontCssVar(mark.fontStyle),
          fontSize: Math.min(boxHeight * 0.95, 30),
          lineHeight: 1,
        }}
      >
        {mark.fullName}
      </span>
    );
  }
  return (
    <span
      className="leading-none whitespace-nowrap text-[hsl(220,55%,25%)] dark:text-[hsl(35,90%,80%)]"
      style={{
        fontFamily: fontCssVar(mark.fontStyle),
        fontSize: Math.min(boxHeight * 0.85, 22),
        lineHeight: 1,
      }}
    >
      {mark.initials}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Adopt signature dialog
// ────────────────────────────────────────────────────────────────────────────

function AdoptSignatureDialog({
  open,
  onOpenChange,
  defaultName,
  defaultInitials,
  defaultFont,
  onAdopt,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  defaultName: string;
  defaultInitials: string;
  defaultFont: FontStyleKey;
  onAdopt: (m: SignatureMark) => void;
}) {
  const [tab, setTab] = useState<"type" | "draw">("draw");
  const [fullName, setFullName] = useState(defaultName);
  const [initials, setInitials] = useState(defaultInitials);
  const [initialsTouched, setInitialsTouched] = useState(false);
  const [fontStyle, setFontStyle] = useState<FontStyleKey>(defaultFont);

  const sigPadRef = useRef<SigPadHandle | null>(null);
  const initPadRef = useRef<SigPadHandle | null>(null);

  // Reset values whenever dialog opens
  useEffect(() => {
    if (open) {
      setFullName(defaultName);
      setInitials(defaultInitials);
      setInitialsTouched(false);
      setFontStyle(defaultFont);
      setTab("draw");
    }
  }, [open, defaultName, defaultInitials, defaultFont]);

  // Auto-derive initials until user manually edits the field
  useEffect(() => {
    if (initialsTouched) return;
    setInitials(deriveInitials(fullName));
  }, [fullName, initialsTouched]);

  const fullNameTrim = fullName.trim();
  const initialsTrim = initials.trim();
  const typeReady = fullNameTrim.length > 0 && initialsTrim.length > 0;

  const handleAdopt = () => {
    if (tab === "type") {
      if (!typeReady) return;
      onAdopt({
        mode: "type",
        fullName: fullNameTrim,
        initials: initialsTrim.toUpperCase(),
        fontStyle,
      });
      return;
    }
    // draw
    const sigUrl = sigPadRef.current?.toDataURL();
    const initUrl = initPadRef.current?.toDataURL();
    if (!sigUrl || !initUrl) return;
    onAdopt({
      mode: "draw",
      fullName: fullNameTrim || "Signed",
      initials: (initialsTrim || "S").toUpperCase(),
      fontStyle,
      signatureDataUrl: sigUrl,
      initialsDataUrl: initUrl,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] sm:max-w-[640px] w-[calc(100vw-24px)] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Adopt your signature</DialogTitle>
          <DialogDescription>
            This signature and these initials will be applied wherever you sign on this contract.
            You can re-adopt at any time before submitting.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as "type" | "draw")} className="mt-4">
          <TabsList>
            <TabsTrigger value="type" data-testid="tab-type">Type</TabsTrigger>
            <TabsTrigger value="draw" data-testid="tab-draw">Draw</TabsTrigger>
          </TabsList>

          <TabsContent value="type" className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3 sm:gap-4 mb-3">
              <div>
                <Label htmlFor="adopt-name" className="text-xs">Full name</Label>
                <Input
                  id="adopt-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="mt-1.5"
                  data-testid="input-adopt-name"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="adopt-initials" className="text-xs">Initials</Label>
                <Input
                  id="adopt-initials"
                  value={initials}
                  maxLength={4}
                  onChange={(e) => {
                    setInitialsTouched(true);
                    setInitials(e.target.value.toUpperCase());
                  }}
                  className="mt-1.5 uppercase tracking-wider"
                  data-testid="input-adopt-initials"
                />
              </div>
            </div>
            {/* Style picker */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {FONT_STYLES.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFontStyle(f.key)}
                  className={
                    "border rounded-md p-2 text-center transition " +
                    (fontStyle === f.key
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/50")
                  }
                  data-testid={`font-${f.key}`}
                >
                  <div
                    className="text-2xl leading-tight"
                    style={{ fontFamily: f.cssVar }}
                  >
                    {fullNameTrim ? fullNameTrim.split(" ")[0] || "Sample" : "Sample"}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">{f.label}</div>
                </button>
              ))}
            </div>

            {/* Live preview */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3">
              <div className="rounded-md border border-border bg-card px-4 py-5 min-h-[88px] flex items-center justify-center">
                <span
                  className="text-[hsl(220,55%,25%)] dark:text-[hsl(35,90%,80%)] leading-none whitespace-nowrap"
                  style={{ fontFamily: fontCssVar(fontStyle), fontSize: 38, lineHeight: 1 }}
                  data-testid="preview-signature"
                >
                  {fullNameTrim || "Your name"}
                </span>
              </div>
              <div className="rounded-md border border-border bg-card px-3 py-5 min-h-[88px] flex items-center justify-center">
                <span
                  className="text-[hsl(220,55%,25%)] dark:text-[hsl(35,90%,80%)] leading-none uppercase tracking-wider"
                  style={{ fontFamily: fontCssVar(fontStyle), fontSize: 30, lineHeight: 1 }}
                  data-testid="preview-initials"
                >
                  {initialsTrim || "AB"}
                </span>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="draw" className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
              <div>
                <div className="text-[11px] text-muted-foreground mb-1">Signature</div>
                <SigPad ref={sigPadRef} testId="pad-signature" />
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground mb-1">Initials</div>
                <SigPad ref={initPadRef} small testId="pad-initials" />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-5 gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-sm text-muted-foreground hover:text-foreground"
            data-testid="button-adopt-cancel"
          >
            Cancel
          </button>
          <Button
            onClick={handleAdopt}
            disabled={tab === "type" && !typeReady}
            data-testid="button-adopt"
          >
            Adopt and sign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Hand-rolled signature pad
// ────────────────────────────────────────────────────────────────────────────

interface SigPadHandle {
  toDataURL: () => string | null;
  clear: () => void;
}

const SigPad = forwardRef<SigPadHandle, { small?: boolean; testId?: string }>(
  function SigPad({ small, testId }, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawing = useRef(false);
    const last = useRef<{ x: number; y: number } | null>(null);
    const [hasInk, setHasInk] = useState(false);

    // Set up canvas
    useEffect(() => {
      const c = canvasRef.current;
      if (!c) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cssWidth = c.clientWidth;
      const cssHeight = c.clientHeight;
      c.width = Math.floor(cssWidth * dpr);
      c.height = Math.floor(cssHeight * dpr);
      const ctx = c.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0d2050";
      ctx.lineWidth = small ? 2.4 : 2.8;
    }, [small]);

    const getPos = (e: React.PointerEvent) => {
      const c = canvasRef.current!;
      const rect = c.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onDown = (e: React.PointerEvent) => {
      const c = canvasRef.current;
      if (!c) return;
      c.setPointerCapture(e.pointerId);
      drawing.current = true;
      last.current = getPos(e);
    };
    const onMove = (e: React.PointerEvent) => {
      if (!drawing.current) return;
      const c = canvasRef.current!;
      const ctx = c.getContext("2d");
      if (!ctx) return;
      const p = getPos(e);
      const l = last.current!;
      ctx.beginPath();
      ctx.moveTo(l.x, l.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      last.current = p;
      if (!hasInk) setHasInk(true);
    };
    const onUp = (e: React.PointerEvent) => {
      drawing.current = false;
      last.current = null;
      try { canvasRef.current?.releasePointerCapture(e.pointerId); } catch {}
    };

    const clear = () => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      if (!ctx) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.restore();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0d2050";
      ctx.lineWidth = small ? 2.4 : 2.8;
      setHasInk(false);
    };

    useImperativeHandle(ref, () => ({
      toDataURL: () => {
        const c = canvasRef.current;
        if (!c) return null;
        if (!hasInk) return null;
        return c.toDataURL("image/png");
      },
      clear,
    }));

    return (
      <div>
        <canvas
          ref={canvasRef}
          className={
            "block rounded-md border border-dashed border-border bg-white touch-none w-full " +
            (small ? "h-[140px]" : "h-[160px]")
          }
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          data-testid={testId}
        />
        <div className="mt-1.5 flex justify-end">
          <button
            type="button"
            onClick={clear}
            className="text-[11px] text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        </div>
      </div>
    );
  },
);


// ────────────────────────────────────────────────────────────────────────────
// Official Signature Page (DOM, will be reproduced server-side in PDF)
// ────────────────────────────────────────────────────────────────────────────

function OfficialSignaturePage({
  contractTitle,
  porCntr,
  signerName,
  signerEmail,
  mark,
  today,
  eConsent,
  setEConsent,
  termsConsent,
  setTermsConsent,
  canSubmit,
  isSubmitting,
  onSubmit,
  allInitialsFilled,
  signatureFilled,
  revisionPaymentDueCents,
  savedCard,
  revisionAlreadyCharged,
  revisionAlreadyChargedAmount,
  chargeFailure,
  threeDSecure,
  onRetryWithNewCard,
  paymentRequired,
}: {
  contractTitle: string;
  porCntr: string | null;
  signerName: string;
  signerEmail: string;
  mark: SignatureMark | null;
  today: string;
  eConsent: boolean;
  setEConsent: (v: boolean) => void;
  termsConsent: boolean;
  setTermsConsent: (v: boolean) => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  allInitialsFilled: boolean;
  signatureFilled: boolean;
  revisionPaymentDueCents: number;
  paymentRequired: boolean;
  savedCard: { brand: string; last4: string } | null;
  revisionAlreadyCharged: boolean;
  revisionAlreadyChargedAmount: number;
  chargeFailure: { message: string; code?: string } | null;
  threeDSecure: null | { status: "running" } | { status: "failed"; message: string };
  onRetryWithNewCard: () => void;
}) {
  const sigStyle = mark ? fontCssVar(mark.fontStyle) : "'Dancing Script', cursive";
  const sigText = mark?.fullName ?? "—";
  const initText = mark?.initials ?? "—";

  return (
    <div className="mt-8" data-testid="official-signature-page">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5 ml-1">
        Appended page
      </div>
      <div className="bg-white dark:bg-card border border-card-border shadow-md rounded-sm p-8 sm:p-12 text-[#1a1a1a] dark:text-foreground">
        <div className="border-b border-neutral-300 pb-4 mb-6">
          <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Source Serif 4', serif" }}>
            Official Signature Page
          </h2>
          <p className="text-xs text-neutral-500 dark:text-muted-foreground mt-1">
            This page becomes part of your signed contract.
          </p>
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm mb-6">
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-neutral-500 dark:text-muted-foreground">Contract</dt>
            <dd className="mt-0.5 font-medium" data-testid="text-official-contract">
              {porCntr ? `Contract #${porCntr} — ` : ""}{contractTitle}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-neutral-500 dark:text-muted-foreground">Signer</dt>
            <dd className="mt-0.5 font-medium">
              {signerName}
              <div className="text-xs text-neutral-500 dark:text-muted-foreground font-normal">{signerEmail}</div>
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-neutral-500 dark:text-muted-foreground">Date</dt>
            <dd className="mt-0.5 font-medium">{today}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-neutral-500 dark:text-muted-foreground">Counterparty</dt>
            <dd className="mt-0.5 font-medium">Sun Rental Center</dd>
          </div>
        </dl>

        <div
          className={
            "rounded-lg border-2 px-4 py-4 mb-6 transition-colors " +
            (eConsent
              ? "border-emerald-400/70 bg-emerald-50/60 dark:bg-emerald-500/10"
              : "border-amber-400 bg-amber-200/50 dark:bg-amber-300/15")
          }
        >
          <div className="text-[10px] uppercase tracking-wider text-neutral-600 dark:text-muted-foreground mb-1.5 font-semibold">
            1. Consent to do business electronically
          </div>
          <p className="text-[13px] leading-relaxed text-neutral-800 dark:text-muted-foreground">
            Under the U.S. ESIGN Act and the Ohio Uniform Electronic Transactions Act, you may sign
            this rental contract electronically only if you affirmatively consent to the use of
            electronic records and signatures. You confirm you have the ability to access and
            retain a copy of the signed contract in PDF form, and that you may withdraw this consent
            before signing by closing this page.
          </p>
          <label
            className={
              "mt-3 flex items-start gap-3 cursor-pointer rounded-md p-2 -mx-2 transition " +
              (eConsent ? "" : "hover:bg-amber-300/40 dark:hover:bg-amber-300/25")
            }
          >
            <Checkbox
              checked={eConsent}
              onCheckedChange={(v) => setEConsent(!!v)}
              className="mt-0.5 h-5 w-5 border-2 border-neutral-700 dark:border-foreground"
              data-testid="checkbox-econsent"
            />
            <span className="text-[15px] font-semibold text-neutral-900 dark:text-foreground leading-snug">
              I consent to use electronic records and electronic signatures for this transaction.
            </span>
          </label>
        </div>

        <div
          className={
            "rounded-lg border-2 px-4 py-4 mb-6 transition-colors " +
            (termsConsent
              ? "border-emerald-400/70 bg-emerald-50/60 dark:bg-emerald-500/10"
              : "border-amber-400 bg-amber-200/50 dark:bg-amber-300/15")
          }
        >
          <div className="text-[10px] uppercase tracking-wider text-neutral-600 dark:text-muted-foreground mb-1.5 font-semibold">
            2. Agreement to contract terms
          </div>
          <p className="text-[13px] leading-relaxed text-neutral-800 dark:text-muted-foreground">
            By checking the box below and clicking Sign, you confirm that you have read the rental
            contract above and agree to be legally bound by its terms. Your electronic signature
            will have the same legal effect as a handwritten signature.
          </p>
          <label
            className={
              "mt-3 flex items-start gap-3 cursor-pointer rounded-md p-2 -mx-2 transition " +
              (termsConsent ? "" : "hover:bg-amber-300/40 dark:hover:bg-amber-300/25")
            }
          >
            <Checkbox
              checked={termsConsent}
              onCheckedChange={(v) => setTermsConsent(!!v)}
              className="mt-0.5 h-5 w-5 border-2 border-neutral-700 dark:border-foreground"
              data-testid="checkbox-terms"
            />
            <span className="text-[15px] font-semibold text-neutral-900 dark:text-foreground leading-snug">
              I have read and agree to be legally bound by the rental contract terms.
            </span>
          </label>
        </div>

        {/* Signature blocks */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-6 sm:gap-10 mt-6">
          <div>
            <div className="border-b-2 border-neutral-700 dark:border-foreground pb-1 min-h-[64px] flex items-end" data-testid="block-signature">
              {mark?.mode === "draw" && mark.signatureDataUrl ? (
                <img src={mark.signatureDataUrl} alt="Signature" className="h-14 object-contain" />
              ) : (
                <span
                  className="text-[#0d2050] dark:text-[hsl(35,90%,80%)] leading-none whitespace-nowrap"
                  style={{ fontFamily: sigStyle, fontSize: 36, lineHeight: 1 }}
                >
                  {sigText}
                </span>
              )}
            </div>
            <div className="text-[11px] text-neutral-500 dark:text-muted-foreground mt-1.5">
              Signed by {signerName} on {today}
            </div>
          </div>
          <div>
            <div className="border-b-2 border-neutral-700 dark:border-foreground pb-1 min-h-[64px] flex items-end" data-testid="block-initials">
              {mark?.mode === "draw" && mark.initialsDataUrl ? (
                <img src={mark.initialsDataUrl} alt="Initials" className="h-14 object-contain" />
              ) : (
                <span
                  className="text-[#0d2050] dark:text-[hsl(35,90%,80%)] leading-none uppercase tracking-wider"
                  style={{ fontFamily: sigStyle, fontSize: 30, lineHeight: 1 }}
                >
                  {initText}
                </span>
              )}
            </div>
            <div className="text-[11px] text-neutral-500 dark:text-muted-foreground mt-1.5">Initials</div>
          </div>
        </div>

        {/* "Charge now" path — deposit was already collected at resend time.
            Show a small confirmation instead of the auto-charge disclosure. */}
        {revisionAlreadyCharged && revisionAlreadyChargedAmount > 0 && (
          <div
            className="mt-6 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-3.5 flex items-start gap-3"
            data-testid="banner-revision-already-charged"
          >
            <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
            <div className="text-[13px] leading-relaxed text-foreground">
              <div className="font-semibold">
                Additional deposit of{" "}
                <span data-testid="text-already-charged-amount">
                  {formatMoney(revisionAlreadyChargedAmount)}
                </span>{" "}
                already received — thank you.
              </div>
              <div className="text-muted-foreground mt-0.5">
                Just sign below to finalize the updated contract. No further
                payment is needed.
              </div>
            </div>
          </div>
        )}

        {/* Saved-card auto-charge disclosure (revision flows where deposit was already collected) */}
        {!revisionAlreadyCharged && revisionPaymentDueCents > 0 && savedCard && !chargeFailure && (
          <div
            className="mt-6 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3.5 flex items-start gap-3"
            data-testid="banner-revision-autocharge"
          >
            <CreditCard className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div className="text-[13px] leading-relaxed text-foreground">
              <div className="font-semibold">
                Your card on file ({savedCard.brand} ending {savedCard.last4}) will be charged{" "}
                <span data-testid="text-revision-amount">{formatMoney(revisionPaymentDueCents)}</span>{" "}
                when you sign.
              </div>
              <div className="text-muted-foreground mt-0.5">
                This is the additional deposit due on the updated contract. No further action required.
              </div>
            </div>
          </div>
        )}

        {/* Charge failure banner — hard decline or non-3DS off_session failure */}
        {chargeFailure && (
          <div
            className="mt-6 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3.5"
            data-testid="banner-charge-failure"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
              <div className="text-[13px] leading-relaxed flex-1">
                <div className="font-semibold text-destructive">
                  We couldn't charge your card on file.
                </div>
                <div className="text-muted-foreground mt-0.5" data-testid="text-charge-failure-message">
                  {chargeFailure.message ||
                    "The payment was declined. Please use a different card to complete the deposit."}
                </div>
                <div className="mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onRetryWithNewCard}
                    data-testid="button-retry-new-card"
                  >
                    Pay with a different card
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 3DS verification overlay/inline status */}
        {threeDSecure?.status === "running" && (
          <div
            className="mt-6 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3.5 flex items-center gap-3"
            data-testid="banner-3ds-running"
          >
            <div className="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin shrink-0" />
            <div className="text-[13px] leading-relaxed text-foreground">
              <div className="font-semibold">Verifying your card with your bank…</div>
              <div className="text-muted-foreground mt-0.5">
                Please complete any prompts in the popup. This usually takes a few seconds.
              </div>
            </div>
          </div>
        )}

        {/* Sign Document button — directly below the final signature line */}
        <div className="mt-8">
          <Button
            size="lg"
            className="w-full h-12 text-base gap-2"
            disabled={!canSubmit || isSubmitting}
            onClick={onSubmit}
            data-testid="button-sign"
          >
            <CheckCircle2 className="h-5 w-5" />
            {isSubmitting
              ? "Submitting…"
              : revisionAlreadyCharged
                ? "Sign updated contract"
                : revisionPaymentDueCents > 0 && savedCard && !chargeFailure
                  ? `Sign and charge ${formatMoney(revisionPaymentDueCents)}`
                  : !paymentRequired
                    ? "Sign document"
                    : "Sign and proceed to payment"}
          </Button>
          {!canSubmit && (
            <div className="text-[11px] text-muted-foreground text-center mt-2">
              {!mark
                ? "Click any orange box on the contract to adopt your signature."
                : !allInitialsFilled
                  ? "Click each initial box on the contract above."
                  : !signatureFilled
                    ? "Click the signature box on the contract above."
                    : !eConsent
                      ? "Check the electronic-records consent box to continue."
                      : !termsConsent
                        ? "Check the agreement-to-terms box to enable signing."
                        : ""}
            </div>
          )}
          <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3" /> Secure signing · audit-logged
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Shell
// ────────────────────────────────────────────────────────────────────────────

function SignShell({
  children,
  headerExtras,
  titleSlot,
  rightSlot,
}: {
  children: React.ReactNode;
  headerExtras?: React.ReactNode;
  titleSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b border-border bg-card/95 backdrop-blur-sm sticky top-0 z-20">
        {/* Constrain header content to the PDF's max width so brand / title / progress
            line up with the document edges below. */}
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          {/* Top row.
              Mobile: brand (auto, never shrinks) | title (1fr, wraps if long, right-aligned).
              Desktop: brand | title | progress (3 cols, equal sides keep title centered). */}
          <div className="min-h-14 py-2 grid grid-cols-[auto_minmax(0,1fr)] md:grid-cols-[1fr_auto_1fr] items-center gap-4">
            {/* Sun Rental brand cluster (left) — fixed-width on mobile so the title can never push into it */}
            <div className="flex items-center gap-2.5 shrink-0">
              <SunLogo size={28} />
              <div className="leading-tight">
                <div className="text-[14px] font-semibold tracking-tight whitespace-nowrap">Sun Rental</div>
                <div className="text-[10px] text-muted-foreground -mt-0.5 whitespace-nowrap">Contract for signature</div>
              </div>
            </div>

            {/* Contract title — right-aligned & wrapping on mobile, centered on desktop */}
            <div className="min-w-0 text-right md:text-center md:justify-self-center">{titleSlot}</div>

            {/* Progress (right) — desktop only */}
            <div className="hidden md:flex justify-end">
              {rightSlot}
            </div>
          </div>

          {/* Mobile: instructions first, then progress beneath them */}
          {headerExtras && (
            <div className="border-t border-border/60">{headerExtras}</div>
          )}

          {rightSlot && (
            <div className="md:hidden border-t border-border/60 py-2 -mx-4 sm:-mx-6 px-0">
              {rightSlot}
            </div>
          )}
        </div>
      </header>

      <div className="flex-1">{children}</div>

      <footer className="border-t border-border bg-card/40">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-5 text-[11px] text-muted-foreground text-center leading-relaxed">
          <div>
            <span className="font-medium text-foreground">Sun Rental Center</span> · 7467 Mentor Ave, Mentor OH 44060
          </div>
          <div className="mt-0.5">
            440-942-6106 ·{" "}
            <a className="hover:underline" href="mailto:events@sunrental.com">
              events@sunrental.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
